import { Icon } from "@ui/components/Icon/Icon";
import { pillGroupItem } from "@ui/components/PillGroup";
import { SoftButton } from "@ui/components/SoftButton";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { DialogTrigger } from "@ui/primitives/Popover";
import { composeRenderProps } from "@ui/primitives/shared";
import type { ReactNode } from "react";
import { SearchBarPopoverShell } from "./SearchBarPopoverShell";

const sectionContent = tv({
  slots: {
    inner: "relative z-10 flex min-w-0 items-center gap-3",
    icon: "flex size-8 shrink-0 items-center justify-center [&>svg]:size-8",
    text: "flex h-8 min-w-0 flex-1 flex-col justify-center",
    heading: "text-base font-semibold leading-4 tracking-[-0.16px] text-neutral-900",
    description: "max-w-full truncate text-base font-normal leading-4 text-neutral-600",
  },
  variants: {
    isActive: {
      true: { description: "text-neutral-900" },
    },
  },
});

export interface SearchBarSectionProps {
  icon: ReactNode;
  heading: string;
  description: ReactNode;
  isActive?: boolean;
  popoverContent: ReactNode;
  popoverAriaLabel?: string;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** When provided alongside `isActive`, a soft X button renders inside the section
   *  to clear the active value. The trigger pill itself remains the popover opener;
   *  the clear sits as a sibling so it intercepts clicks without nested-button HTML. */
  onClear?: () => void;
  clearAriaLabel?: string;
}

export function SearchBarSection({
  icon,
  heading,
  description,
  isActive,
  popoverContent,
  popoverAriaLabel,
  isOpen,
  onOpenChange,
  onClear,
  clearAriaLabel,
}: SearchBarSectionProps) {
  const styles = sectionContent({ isActive });
  const showClear = isActive && !!onClear;

  // Section root carries the right-divider so `last:` evaluates against actual sibling
  // sections in PillGroupItems. The trigger Button is now a child of this wrapper, so
  // `last:after:hidden` on the Button itself wouldn't fire correctly — its `after:` is
  // suppressed via the className override below.
  return (
    <div
      className={[
        "relative flex h-15 min-w-0 shrink grow-0 basis-60 items-center",
        "after:pointer-events-none after:absolute after:top-1/2 after:right-[-8.5px] after:h-8 after:w-px after:-translate-y-1/2 after:bg-neutral-100 after:transition-opacity after:duration-150",
        "last:after:hidden",
        "has-[[data-hovered]]:after:opacity-0 has-[[data-pressed]]:after:opacity-0 has-[[data-focus-visible]]:after:opacity-0",
        "has-[+_*[data-hovered]]:after:opacity-0 has-[+_*[data-pressed]]:after:opacity-0 has-[+_*[data-focus-visible]]:after:opacity-0",
      ].join(" ")}
    >
      <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
        <Button
          className={composeRenderProps("min-w-0 flex-1", (className, renderProps) =>
            pillGroupItem({
              ...renderProps,
              className: `${className ?? ""} after:hidden ${showClear ? "pr-10" : ""}`,
            }),
          )}
        >
          <span className={styles.inner()}>
            <span aria-hidden="true" className={styles.icon()}>
              {icon}
            </span>
            <span className={styles.text()}>
              <span className={styles.heading()}>{heading}</span>
              <span className={styles.description()}>{description}</span>
            </span>
          </span>
        </Button>
        <SearchBarPopoverShell ariaLabel={popoverAriaLabel ?? heading}>{popoverContent}</SearchBarPopoverShell>
      </DialogTrigger>
      {showClear ? (
        <SoftButton
          aria-label={clearAriaLabel ?? `Clear ${heading}`}
          className="absolute right-2 top-1/2 z-10 size-6 -translate-y-1/2"
          onPress={onClear}
        >
          <Icon name="exit" size="16" />
        </SoftButton>
      ) : null}
    </div>
  );
}
